'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Region {
  id: string;
  name: string;
  storiesCount: number;
}

interface InteractiveMapProps {
  regions: Region[];
  onRegionSelect: (regionId: string) => void;
}

export default function InteractiveMap({ regions, onRegionSelect }: InteractiveMapProps) {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  const handleRegionClick = (regionId: string) => {
    setSelectedRegion(regionId);
    onRegionSelect(regionId);
  };

  return (
    <div className="bg-card rounded-xl p-8 shadow-warm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-heading font-bold text-text-primary">
          Explore India's Story Map
        </h3>
        <Icon name="MapIcon" size={28} className="text-primary" />
      </div>

      <div className="relative bg-muted rounded-lg p-8 min-h-96 flex items-center justify-center">
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon name="GlobeAltIcon" size={120} className="text-primary/10" />
        </div>

        <div className="relative grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full">
          {regions.map((region) => (
            <button
              key={region.id}
              onClick={() => handleRegionClick(region.id)}
              className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                selectedRegion === region.id
                  ? 'bg-primary text-primary-foreground border-primary shadow-warm'
                  : 'bg-card text-text-primary border-border hover:border-primary hover:shadow-warm'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="MapPinIcon" size={18} />
                <span className="font-heading font-bold text-sm">{region.name}</span>
              </div>
              <p className="text-xs opacity-80">
                {region.storiesCount.toLocaleString('en-IN')} Stories
              </p>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 p-4 bg-accent/10 rounded-lg border border-accent/20">
        <p className="text-sm text-text-primary flex items-center">
          <Icon name="InformationCircleIcon" size={18} className="mr-2 text-accent" />
          Click on any region to explore local stories and creators
        </p>
      </div>
    </div>
  );
}